import React, {createContext, useContext, useState, useCallback, ReactNode} from "react";

interface HelpContextType {
  isModalOpen: boolean;
  currentTopicId: string | null;
  openHelp: (topicId?: string) => void;
  closeHelp: () => void;
  navigateToTopic: (topicId: string) => void;
}

const HelpContext = createContext<HelpContextType | undefined>(undefined);

export const useHelp = () => {
  const context = useContext(HelpContext);
  if (!context) {
    throw new Error("useHelp must be used within a HelpProvider");
  }
  return context;
};

interface HelpProviderProps {
  children: ReactNode;
}

export const HelpProvider: React.FC<HelpProviderProps> = ({children}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentTopicId, setCurrentTopicId] = useState<string | null>(null);

  const openHelp = useCallback((topicId?: string) => {
    setCurrentTopicId(topicId || null);
    setIsModalOpen(true);
  }, []);

  const closeHelp = useCallback(() => {
    setIsModalOpen(false);
    // Keep currentTopicId for a moment to prevent flash during close animation
    setTimeout(() => setCurrentTopicId(null), 300);
  }, []);

  const navigateToTopic = useCallback((topicId: string) => {
    setCurrentTopicId(topicId);
    // Scroll to the topic after a brief delay to ensure DOM is ready
    setTimeout(() => {
      const element = document.getElementById(`help-topic-${topicId}`);
      if (element) {
        element.scrollIntoView({behavior: "smooth", block: "start"});
      }
    }, 100);
  }, []);

  const value = {
    isModalOpen,
    currentTopicId,
    openHelp,
    closeHelp,
    navigateToTopic
  };

  return <HelpContext.Provider value={value}>{children}</HelpContext.Provider>;
};
